/**
 * Lifecycle state of the active organization (US-E10-06). Emitted values:
 * `PENDING_PARTNER_LAB_SELECTION` (no partner DELABO lab assigned yet),
 * `PENDING_PARTNER_LAB_CONFIRMATION` (partner lab selected, awaiting confirmation), `ACTIVE`
 * (onboarding complete, order creation allowed), `DISABLED` (the account is deactivated — a
 * generic state, not limited to a declined partner-lab assignment; the account does not
 * re-enter partner-lab selection, order creation remains blocked). Reserved for future use, not
 * yet emitted and deliberately not part of this union yet: `DELETED`. Response field only,
 * never a signed JWT claim.
 *
 * This union must stay bit-for-bit congruent with the generated
 * `components['schemas']['OrganizationState']` literal union (`schema.d.ts`) — widen it only in
 * lockstep with an OpenAPI contract change, never speculatively.
 */
export type OrganizationState =
  | 'PENDING_PARTNER_LAB_SELECTION'
  | 'PENDING_PARTNER_LAB_CONFIRMATION'
  | 'ACTIVE'
  | 'DISABLED';

const KNOWN_ORGANIZATION_STATES: readonly OrganizationState[] = [
  'PENDING_PARTNER_LAB_SELECTION',
  'PENDING_PARTNER_LAB_CONFIRMATION',
  'ACTIVE',
  'DISABLED',
];

/**
 * Parses a raw wire value into a known {@link OrganizationState}, fail-closed: any value that is
 * not currently a member of the closed union — a future state rolled out on the backend before
 * this build knows about it, or a malformed/corrupted response — resolves to `null` rather than
 * defaulting to `'ACTIVE'`. This is deliberate: `'ACTIVE'` is the *only* state that unlocks order
 * creation (US-E10-06, AK-9), so silently coercing an unrecognized value to it would permissively
 * unlock a guard that is supposed to fail closed. Callers comparing against `'ACTIVE'` therefore
 * stay locked for `null` the same way they do for any other non-`'ACTIVE'` state.
 */
export function parseOrganizationState(raw: string): OrganizationState | null {
  return (KNOWN_ORGANIZATION_STATES as readonly string[]).includes(raw) ? (raw as OrganizationState) : null;
}

/**
 * shadcn Badge color variants the shared `OrganizationStatusBadge` renders an
 * {@link OrganizationState} with. `outline` is reserved for the fail-closed fallback (an
 * unrecognized or `null` state) and is therefore intentionally not assigned to any known state in
 * {@link ORGANIZATION_STATE_DISPLAY}.
 */
export type OrganizationStatusBadgeVariant = 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';

/**
 * The i18n message keys {@link ORGANIZATION_STATE_DISPLAY} maps an `OrganizationState` to, scoped
 * to the shared `organizationState` i18n namespace (`shared/i18n/organizationState/de.json`).
 */
type OrganizationStateI18nKey = 'status_registered' | 'status_waiting' | 'status_active' | 'status_disabled';

/** i18n key and Badge variant for one known {@link OrganizationState}. */
export interface OrganizationStateDisplay {
  readonly i18nKey: OrganizationStateI18nKey;
  readonly variant: OrganizationStatusBadgeVariant;
}

/**
 * Maps each known `OrganizationState` to its i18n message key in the shared `organizationState`
 * namespace and to the shadcn Badge variant approved for status display across the app (solution
 * "Label-Mapping Kontostatus"): `PENDING_PARTNER_LAB_SELECTION` renders `secondary` (registered,
 * no urgent action yet), `PENDING_PARTNER_LAB_CONFIRMATION` renders `warning` (action pending),
 * `ACTIVE` renders `success`, `DISABLED` renders `destructive` (the account is deactivated —
 * declined assignments are the primary path today, but the state itself is generic). Only the
 * i18n **key**, never the literal German text, is kept here — the wording is single-sourced in
 * the i18n resource file. This is the one vocabulary source for organization-state display
 * (QK-SW-20); domain features must not redefine their own state-to-label map.
 */
const ORGANIZATION_STATE_DISPLAY: Readonly<Record<OrganizationState, OrganizationStateDisplay>> = {
  PENDING_PARTNER_LAB_SELECTION: { i18nKey: 'status_registered', variant: 'secondary' },
  PENDING_PARTNER_LAB_CONFIRMATION: { i18nKey: 'status_waiting', variant: 'warning' },
  ACTIVE: { i18nKey: 'status_active', variant: 'success' },
  DISABLED: { i18nKey: 'status_disabled', variant: 'destructive' },
};

/**
 * Looks up the i18n key and Badge variant for an organization's `OrganizationState`. Returns
 * `null` for any value outside the closed union — a future backend state this build does not yet
 * know about, or a malformed response. Callers (e.g. the shared `OrganizationStatusBadge`) must
 * render a fail-closed fallback (raw value, neutral `outline` variant) for `null` rather than
 * guessing a color, mirroring the fail-closed contract of {@link parseOrganizationState}.
 */
export function organizationStateDisplay(state: OrganizationState): OrganizationStateDisplay | null {
  return ORGANIZATION_STATE_DISPLAY[state] ?? null;
}
